import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  InputAdornment, LinearProgress, Paper, Stack, Snackbar, Switch,
  TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography, alpha, useTheme, Divider, Skeleton, Alert,
} from '@mui/material';
import {
  Add, ContentCopy, DeleteOutline, Edit, Link as LinkIcon, Lock, Public, RestartAlt, Save, Search, Security, Shield,
  CheckCircle, Undo, InfoOutlined,
} from '@mui/icons-material';
import api from '../../services/api';
import PageShell from '../../components/layout/PageShell';
import PageHeader from '../../components/layout/PageHeader';
import { useSelector } from 'react-redux';
import {
  ADMIN_PAGE_PRIVILEGES,
  getPageActions,
  groupPagePrivilegesBySection,
  pagePermissionKeys,
  privilegeActionMeta,
  type AdminPagePrivilege,
  type PrivilegeActionDef,
} from '../../constants/adminPagePrivileges';
import {
  GRANTABLE_PAGES,
  ROLE_DESCRIPTION_MAX,
  allGrantablePermissions,
  countChanges,
  expandWildcardsFor,
  normalizeRolePermissions,
  pageHasAction,
  pageHasView,
  roleCoverage,
  sanitizeRoleName,
  sanitizeRoleSlug,
  sharedScreens,
  slugFromRoleName,
  validateRoleName,
  validateRoleSlug,
} from '../../utils/rolePrivileges';
import { useI18n } from '../../i18n/I18nProvider';
import { translateUiText } from '../../i18n/translateUiText';
import {
  DEMO_WRITE_BLOCKED_MESSAGE,
  isDemoDashboardWriteProtected,
  notifyDemoWriteBlocked,
} from '../../utils/demoMode';

interface RoleRow {
  _id: string;
  name: string;
  slug: string;
  description?: string;
  permissions: string[];
  isSystem?: boolean;
  isActive?: boolean;
  updatedAt?: string;
  userCount?: number;
  fullAccessLocked?: boolean;
  hasDefaults?: boolean;
  deleteLock?: string | null;
}

interface ConfirmState {
  title: string;
  body: React.ReactNode;
  confirmLabel: string;
  color?: 'primary' | 'error' | 'warning';
  onConfirm: () => void;
}

type MatrixFilter = 'all' | 'granted' | 'not_granted';

const apiError = (err: unknown, fallback: string) =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback;

export default function RolesPrivilegesPage() {
  const theme = useTheme();
  const { t } = useI18n();
  const tl = (s: string) => translateUiText(s, t);
  const queryClient = useQueryClient();
  const tenantName = useSelector((state: { tenant: { companyName?: string } }) => state.tenant.companyName) || 'this tenant';

  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [draftPerms, setDraftPerms] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<MatrixFilter>('all');
  const [roleDialog, setRoleDialog] = useState(false);
  const [editingRole, setEditingRole] = useState<RoleRow | null>(null);
  const [roleForm, setRoleForm] = useState({ name: '', slug: '', description: '', permissions: [] as string[] });
  const [roleTouched, setRoleTouched] = useState({ name: false, slug: false });
  const [roleError, setRoleError] = useState('');
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const [toast, setToast] = useState<{ message: string; severity: 'success' | 'error' | 'info' } | null>(null);
  const [wildcardNote, setWildcardNote] = useState<string[]>([]);
  const [demoBlockedOpen, setDemoBlockedOpen] = useState(false);

  const blockDemoWrite = () => {
    if (!isDemoDashboardWriteProtected()) return false;
    notifyDemoWriteBlocked();
    setDemoBlockedOpen(true);
    return true;
  };

  const { data, isLoading } = useQuery({
    queryKey: ['roles'],
    queryFn: async () => (await api.get('/admin/roles')).data,
  });

  const roles: RoleRow[] = data?.data || [];
  const selectedRole = roles.find((r) => r._id === selectedRoleId) || roles[0];
  const savedPerms = useMemo(
    () => normalizeRolePermissions(selectedRole?.permissions || []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedRole?._id, selectedRole?.updatedAt],
  );

  useEffect(() => {
    if (!selectedRole) return;
    if (selectedRoleId !== selectedRole._id) setSelectedRoleId(selectedRole._id);
    setDraftPerms(savedPerms);
    setWildcardNote([]);
    // Only reset the draft when the role or its saved version changes, not on background refetches.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRole?._id, selectedRole?.updatedAt]);

  const changeCount = useMemo(() => countChanges(draftPerms, savedPerms), [draftPerms, savedPerms]);
  const dirty = changeCount > 0;
  const fullLocked = !!selectedRole?.fullAccessLocked;

  useEffect(() => {
    if (!dirty) return undefined;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  const stats = useMemo(() => roleCoverage(draftPerms), [draftPerms]);

  const sections = useMemo(() => {
    const q = search.trim().toLowerCase();
    return groupPagePrivilegesBySection()
      .map((s) => ({
        ...s,
        allPages: s.pages,
        pages: s.pages.filter((p) => {
          const matches = !q
            || p.pageLabel.toLowerCase().includes(q)
            || s.sectionLabel.toLowerCase().includes(q)
            || p.path.toLowerCase().includes(q)
            || (p.description || '').toLowerCase().includes(q)
            || pagePermissionKeys(p).some((k) => k.toLowerCase().includes(q))
            || getPageActions(p).some((a) => a.label.toLowerCase().includes(q));
          if (!matches) return false;
          if (filter === 'all') return true;
          const granted = !p.platformOnly && pageHasView(draftPerms, p);
          return filter === 'granted' ? granted : !granted;
        }),
      }))
      .filter((s) => s.pages.length > 0);
  }, [search, filter, draftPerms]);
  const matchedScreens = sections.reduce((n, s) => n + s.pages.length, 0);

  const invalidateRoles = () => queryClient.invalidateQueries({ queryKey: ['roles'] });

  const savePermsMutation = useMutation({
    mutationFn: async () => api.put(`/admin/roles/${selectedRole!._id}`, { permissions: draftPerms }),
    onSuccess: async () => {
      await invalidateRoles();
      setWildcardNote([]);
      setToast({ severity: 'success', message: `Saved privileges for ${selectedRole?.name}. Users with this role get the new access on their next request.` });
    },
    onError: (err) => setToast({ severity: 'error', message: apiError(err, 'Could not save privileges') }),
  });

  const saveRoleMutation = useMutation({
    mutationFn: async () => {
      if (editingRole?._id) {
        return api.put(`/admin/roles/${editingRole._id}`, {
          ...(editingRole.isSystem ? {} : { name: roleForm.name.trim() }),
          description: roleForm.description.trim(),
        });
      }
      return api.post('/admin/roles', {
        name: roleForm.name.trim(),
        slug: roleForm.slug,
        description: roleForm.description.trim(),
        permissions: roleForm.permissions,
      });
    },
    onSuccess: async (res) => {
      await invalidateRoles();
      const created = !editingRole;
      setRoleDialog(false);
      setEditingRole(null);
      if (created && res?.data?.data?._id) setSelectedRoleId(res.data.data._id);
      setToast({ severity: 'success', message: created ? `Role "${roleForm.name.trim()}" created` : 'Role details saved' });
    },
    onError: (err) => setRoleError(apiError(err, 'Could not save role')),
  });

  const deleteRoleMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/admin/roles/${id}`),
    onSuccess: async () => {
      setSelectedRoleId('');
      await invalidateRoles();
      setToast({ severity: 'success', message: 'Role deleted' });
    },
    onError: (err) => setToast({ severity: 'error', message: apiError(err, 'Could not delete role') }),
  });

  const resetRoleMutation = useMutation({
    mutationFn: (id: string) => api.post(`/admin/roles/${id}/reset`),
    onSuccess: async () => {
      await invalidateRoles();
      setToast({ severity: 'success', message: 'Role reset to its default permissions' });
    },
    onError: (err) => setToast({ severity: 'error', message: apiError(err, 'Could not reset role') }),
  });

  /** Applies an edit to specific pages, first splitting any module wildcard that covers them. */
  const applyToPages = (pages: AdminPagePrivilege[], edit: (perms: string[]) => string[]) => {
    const { perms, expanded } = expandWildcardsFor(draftPerms.filter((p) => p !== '*'), pages);
    if (expanded.length) setWildcardNote((prev) => Array.from(new Set([...prev, ...expanded])));
    setDraftPerms(Array.from(new Set(edit(perms))).sort());
  };

  const stripKeys = (perms: string[], keys: string[]) => {
    const drop = new Set(keys);
    return perms.filter((p) => !drop.has(p));
  };

  const toggleView = (page: AdminPagePrivilege, on: boolean) => {
    applyToPages([page], (perms) => (on ? [...perms, page.viewPermission] : stripKeys(perms, pagePermissionKeys(page))));
  };

  const setAction = (page: AdminPagePrivilege, action: PrivilegeActionDef, on: boolean) => {
    applyToPages([page], (perms) => (on ? [...perms, page.viewPermission, action.permission] : stripKeys(perms, [action.permission])));
  };

  const toggleAction = (page: AdminPagePrivilege, action: PrivilegeActionDef, on: boolean) => {
    if (on && action.kind === 'delete') {
      setConfirm({
        title: `Grant Delete on ${page.pageLabel}?`,
        body: (
          <Stack spacing={1}>
            <Typography variant="body2">
              Users with <b>{selectedRole?.name}</b> will be able to permanently remove records on {page.pageLabel}.
            </Typography>
            {sharedScreens(page).length > 0 && (
              <Typography variant="body2" color="text.secondary">
                This permission is shared, so Delete is also granted on: {sharedScreens(page).map((p) => p.pageLabel).join(', ')}.
              </Typography>
            )}
          </Stack>
        ),
        confirmLabel: 'Grant Delete',
        color: 'error',
        onConfirm: () => setAction(page, action, true),
      });
      return;
    }
    setAction(page, action, on);
  };

  const sectionKeys = (pages: AdminPagePrivilege[]) => pages.filter((p) => !p.platformOnly).flatMap(pagePermissionKeys);

  const enableSection = (pages: AdminPagePrivilege[]) => {
    const grantable = pages.filter((p) => !p.platformOnly);
    applyToPages(grantable, (perms) => [
      ...perms,
      ...grantable.flatMap((p) => [p.viewPermission, ...getPageActions(p).map((a) => a.permission)]),
    ]);
  };

  const clearSection = (label: string, pages: AdminPagePrivilege[]) => {
    const grantable = pages.filter((p) => !p.platformOnly);
    const keys = sectionKeys(grantable);
    const granted = grantable.filter((p) => pageHasView(draftPerms, p));
    const outside = GRANTABLE_PAGES.filter(
      (p) => !grantable.includes(p) && pageHasView(draftPerms, p) && pagePermissionKeys(p).some((k) => keys.includes(k)),
    );
    setConfirm({
      title: `Clear ${label}?`,
      body: (
        <Stack spacing={1}>
          <Typography variant="body2">
            Removes View and every write action from {granted.length} screen{granted.length === 1 ? '' : 's'} in {label}. Other sections stay as they are.
          </Typography>
          {outside.length > 0 && (
            <Alert severity="warning" sx={{ py: 0 }}>
              Shared permissions — these screens in other sections lose the same access: {outside.map((p) => p.pageLabel).join(', ')}.
            </Alert>
          )}
          <Typography variant="caption" color="text.secondary">Nothing is saved until you click Save Privileges.</Typography>
        </Stack>
      ),
      confirmLabel: 'Clear section',
      color: 'warning',
      onConfirm: () => applyToPages(grantable, (perms) => stripKeys(perms, keys)),
    });
  };

  const requestFullAccess = (on: boolean) => {
    if (fullLocked) return;
    setConfirm(on ? {
      title: 'Turn on Full access?',
      body: (
        <Typography variant="body2">
          <b>{selectedRole?.name}</b> will get View and every action on all {stats.totalScreens} screens ({stats.totalActions} permissions),
          including any screens added later. Platform-only screens stay reserved for the platform Super Admin.
        </Typography>
      ),
      confirmLabel: 'Turn on Full access',
      color: 'warning',
      onConfirm: () => { setDraftPerms(['*']); setWildcardNote([]); },
    } : {
      title: 'Turn off Full access?',
      body: (
        <Typography variant="body2">
          Every screen stays granted individually so you can remove what <b>{selectedRole?.name}</b> shouldn&apos;t have.
          Screens added later will no longer be granted automatically.
        </Typography>
      ),
      confirmLabel: 'Turn off Full access',
      onConfirm: () => setDraftPerms(allGrantablePermissions()),
    });
  };

  const requestSelectRole = (id: string) => {
    if (id === selectedRole?._id) return;
    if (!dirty) { setSelectedRoleId(id); return; }
    setConfirm({
      title: 'Discard unsaved changes?',
      body: (
        <Typography variant="body2">
          You have {changeCount} unsaved change{changeCount === 1 ? '' : 's'} on <b>{selectedRole?.name}</b>. Switching roles discards them.
        </Typography>
      ),
      confirmLabel: 'Discard and switch',
      color: 'warning',
      onConfirm: () => { setDraftPerms(savedPerms); setSelectedRoleId(id); },
    });
  };

  const openRoleDialog = (role: RoleRow | null, clone?: RoleRow) => {
    if (blockDemoWrite()) return;
    setEditingRole(role);
    setRoleError('');
    setRoleTouched({ name: false, slug: false });
    if (role) {
      setRoleForm({ name: role.name, slug: role.slug, description: role.description || '', permissions: role.permissions });
    } else if (clone) {
      const name = sanitizeRoleName(`${clone.name} Copy`).trim();
      setRoleForm({
        name,
        slug: slugFromRoleName(name),
        description: clone.description || '',
        permissions: normalizeRolePermissions(clone === selectedRole ? draftPerms : clone.permissions),
      });
    } else {
      setRoleForm({ name: '', slug: '', description: '', permissions: [] });
    }
    setRoleDialog(true);
  };

  const nameErr = editingRole?.isSystem ? null : validateRoleName(roleForm.name);
  const slugErr = editingRole ? null : validateRoleSlug(roleForm.slug);
  const nameTaken = !editingRole?.isSystem && roles.some(
    (r) => r._id !== editingRole?._id && r.name.toLowerCase() === roleForm.name.trim().toLowerCase(),
  );
  const slugTaken = !editingRole && roles.some((r) => r.slug === roleForm.slug);
  const nameMsg = nameTaken ? 'A role with this name already exists' : roleTouched.name ? nameErr : null;
  const slugMsg = slugTaken ? 'This slug is already used' : roleTouched.slug ? slugErr : null;
  const roleFormBlocked = !!(nameErr || slugErr || nameTaken || slugTaken) || saveRoleMutation.isPending;

  const unlockPages = stats.full ? GRANTABLE_PAGES : GRANTABLE_PAGES.filter((p) => pageHasView(draftPerms, p));

  const roleBadge = (role: RoleRow) => {
    const perms = role._id === selectedRole?._id ? draftPerms : normalizeRolePermissions(role.permissions);
    const c = roleCoverage(perms);
    return {
      label: c.full ? tl('Full') : `${c.viewScreens}/${c.totalScreens}`,
      tip: c.full
        ? `Full access — all ${c.totalScreens} screens with every action`
        : `${c.viewScreens} of ${c.totalScreens} screens with View · ${c.writeScreens} with write actions · ${c.grantedActions} of ${c.totalActions} permissions`,
    };
  };

  return (
    <PageShell>
      <PageHeader
        eyebrow="Configuration"
        title="Roles & Privileges"
        subtitle="Control which admin screens each role can open, with View plus the Add / Edit / Delete options that page actually uses."
        actions={
          <Stack direction="row" spacing={1} alignItems="center">
            <Button variant="outlined" startIcon={<Add />} onClick={() => openRoleDialog(null)}>
              New Role
            </Button>
            {dirty && (
              <Button color="inherit" startIcon={<Undo />} onClick={() => { setDraftPerms(savedPerms); setWildcardNote([]); }}>
                Discard
              </Button>
            )}
            <Tooltip title={dirty ? '' : tl('No unsaved changes')}>
              <span>
                <Button
                  variant="contained"
                  startIcon={<Save />}
                  disabled={!dirty || !selectedRole || savePermsMutation.isPending}
                  onClick={() => {
                    if (blockDemoWrite()) return;
                    savePermsMutation.mutate();
                  }}
                >
                  {dirty ? `${tl('Save Privileges')} (${changeCount})` : tl('Save Privileges')}
                </Button>
              </span>
            </Tooltip>
          </Stack>
        }
      />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '280px minmax(0, 1fr) 260px' },
          gap: 2,
          alignItems: 'start',
        }}
      >
        {/* ── Role list ── */}
        <Paper
          elevation={0}
          sx={{ borderRadius: 3, border: 1, borderColor: 'divider', overflow: 'hidden', position: { lg: 'sticky' }, top: { lg: 16 } }}
        >
          <Box sx={{ px: 2, py: 1.75, bgcolor: alpha(theme.palette.primary.main, 0.04), borderBottom: 1, borderColor: 'divider' }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Security color="primary" fontSize="small" />
              <Typography variant="subtitle2" fontWeight={800}>{tl('Roles')}</Typography>
              <Chip size="small" label={roles.length} sx={{ ml: 'auto', height: 22 }} />
            </Stack>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              {tl('Badge = screens with View / total screens')}
            </Typography>
          </Box>
          <Box sx={{ maxHeight: { lg: 'calc(100vh - 240px)' }, overflow: 'auto', p: 1 }}>
            {isLoading && [1, 2, 3, 4].map((i) => <Skeleton key={i} height={64} sx={{ mb: 1, borderRadius: 2 }} />)}
            {roles.map((role) => {
              const active = selectedRole?._id === role._id;
              const badge = roleBadge(role);
              return (
                <Box
                  key={role._id}
                  onClick={() => requestSelectRole(role._id)}
                  sx={{
                    p: 1.5,
                    mb: 0.75,
                    borderRadius: 2,
                    cursor: 'pointer',
                    border: 1,
                    borderColor: active ? 'primary.main' : 'transparent',
                    bgcolor: active ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) },
                    transition: 'all 0.15s ease',
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Shield sx={{ fontSize: 18, color: active ? 'primary.main' : 'text.secondary' }} />
                    <Box flex={1} minWidth={0}>
                      <Typography variant="body2" fontWeight={700} noWrap>{tl(role.name)}</Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>{role.slug}</Typography>
                    </Box>
                    <Tooltip title={badge.tip}>
                      <Chip size="small" label={badge.label} variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />
                    </Tooltip>
                  </Stack>
                  <Stack direction="row" spacing={0.5} sx={{ mt: 0.75 }} flexWrap="wrap" useFlexGap>
                    {role.isSystem && (
                      <Tooltip title={tl('System role — cannot be deleted, renamed or deactivated; its slug is fixed')}>
                        <Chip size="small" icon={<Lock sx={{ fontSize: '12px !important' }} />} label={tl('System')} sx={{ height: 20, fontSize: '0.65rem' }} />
                      </Tooltip>
                    )}
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`${role.userCount || 0} ${(role.userCount || 0) === 1 ? tl('user') : tl('users')}`}
                      sx={{ height: 20, fontSize: '0.65rem' }}
                    />
                    {active && dirty && (
                      <Chip size="small" color="warning" label={tl('Unsaved')} sx={{ height: 20, fontSize: '0.65rem' }} />
                    )}
                  </Stack>
                </Box>
              );
            })}
          </Box>
        </Paper>

        {/* ── Privilege matrix ── */}
        <Paper elevation={0} sx={{ borderRadius: 3, border: 1, borderColor: 'divider', overflow: 'hidden', minHeight: 520 }}>
          <Box
            sx={{
              px: 2.5,
              py: 2,
              borderBottom: 1,
              borderColor: 'divider',
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.07)}, ${alpha('#0f766e', 0.04)})`,
            }}
          >
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} justifyContent="space-between">
              <Box minWidth={0}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="subtitle1" fontWeight={800} noWrap>
                    {selectedRole ? tl(selectedRole.name) : tl('Select a role')}
                  </Typography>
                  {selectedRole?.isSystem && <Chip size="small" icon={<Lock sx={{ fontSize: '12px !important' }} />} label={tl('System')} sx={{ height: 20 }} />}
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  {stats.viewScreens}/{stats.totalScreens} {tl('screens')} · {stats.grantedActions}/{stats.totalActions} {tl('permissions')} · {stats.coverage}% {tl('coverage')}
                  {selectedRole?.updatedAt ? ` · ${tl('Last saved')} ${new Date(selectedRole.updatedAt).toLocaleString()}` : ''}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                {selectedRole && (
                  <Button size="small" variant="outlined" startIcon={<Edit />} onClick={() => openRoleDialog(selectedRole)}>
                    {tl('Edit details')}
                  </Button>
                )}
                {selectedRole && (
                  <Tooltip title={selectedRole.deleteLock || tl('Delete role')}>
                    <span>
                      <Button
                        size="small"
                        color="error"
                        variant="outlined"
                        startIcon={<DeleteOutline />}
                        disabled={!!selectedRole.deleteLock || deleteRoleMutation.isPending}
                        onClick={() => {
                          if (blockDemoWrite()) return;
                          setConfirm({
                            title: `Delete "${selectedRole.name}"?`,
                            body: <Typography variant="body2">The role and its privileges are removed permanently. No users are assigned to it.</Typography>,
                            confirmLabel: 'Delete role',
                            color: 'error',
                            onConfirm: () => deleteRoleMutation.mutate(selectedRole._id),
                          });
                        }}
                      >
                        {tl('Delete')}
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </Stack>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={stats.coverage}
              sx={{ mt: 1.5, height: 6, borderRadius: 3, bgcolor: alpha(theme.palette.primary.main, 0.12) }}
            />

            {/* Full access */}
            <Box
              sx={{
                mt: 1.5,
                p: 1.25,
                borderRadius: 2,
                border: 1,
                borderColor: stats.full ? 'warning.main' : 'divider',
                bgcolor: stats.full ? alpha(theme.palette.warning.main, 0.08) : 'background.paper',
              }}
            >
              <Stack direction="row" spacing={1.25} alignItems="center">
                <Tooltip title={fullLocked ? tl('The tenant owner role always keeps Full access so the tenant can never be locked out') : ''}>
                  <span>
                    <Switch
                      checked={stats.full}
                      disabled={fullLocked || !selectedRole}
                      onChange={(e) => requestFullAccess(e.target.checked)}
                      color="warning"
                    />
                  </span>
                </Tooltip>
                <Box flex={1} minWidth={0}>
                  <Typography variant="body2" fontWeight={800}>
                    {tl('Full access')} — {stats.full ? tl('ON') : tl('OFF')}
                    {fullLocked && <Lock sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'text-bottom' }} />}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {stats.full
                      ? `All ${stats.totalScreens} screens with View and every action (${stats.totalActions} permissions), including screens added later. Turn off to customise.`
                      : `When on, grants all ${stats.totalScreens} screens with every action, including screens added later.`}
                  </Typography>
                </Box>
              </Stack>
            </Box>

            <Alert severity="info" icon={<Public fontSize="small" />} sx={{ mt: 1.25, py: 0, '& .MuiAlert-message': { fontSize: '0.75rem' } }}>
              {`Tenant scope: this role only applies inside ${tenantName}. Platform-only screens (Tenants, Subscription Plans) are reserved for the platform Super Admin account and can't be granted to any role.`}
            </Alert>
            {wildcardNote.length > 0 && (
              <Alert severity="warning" sx={{ mt: 1, py: 0, '& .MuiAlert-message': { fontSize: '0.75rem' } }}>
                {`Module-wide permission ${wildcardNote.join(', ')} was split into individual screen permissions so this screen could be changed. Access to anything outside this matrix under that module is removed when you save.`}
              </Alert>
            )}
            {dirty && (
              <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: 'block', fontWeight: 700 }}>
                {changeCount} {tl('unsaved change')}{changeCount === 1 ? '' : 's'} — {tl('click Save Privileges to keep them, or Discard')}
              </Typography>
            )}
          </Box>

          <Box sx={{ px: { xs: 1.5, md: 2 }, pt: 1.5 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
              <TextField
                size="small"
                placeholder={tl('Search screens, sections, paths or actions…')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
                sx={{ flex: 1, minWidth: 220 }}
              />
              <ToggleButtonGroup size="small" exclusive value={filter} onChange={(_, v) => v && setFilter(v)}>
                <ToggleButton value="all" sx={{ textTransform: 'none', px: 1.5 }}>{tl('All')}</ToggleButton>
                <ToggleButton value="granted" sx={{ textTransform: 'none', px: 1.5 }}>{tl('Granted')}</ToggleButton>
                <ToggleButton value="not_granted" sx={{ textTransform: 'none', px: 1.5 }}>{tl('Not granted')}</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
              {search || filter !== 'all'
                ? `${matchedScreens} of ${ADMIN_PAGE_PRIVILEGES.length} screens match — search looks at screen name, section, path, description, action and permission key`
                : `${ADMIN_PAGE_PRIVILEGES.length} screens in ${groupPagePrivilegesBySection().length} sections`}
            </Typography>
            <Stack direction="row" spacing={0.75} alignItems="flex-start" sx={{ mt: 1, color: 'text.secondary' }}>
              <InfoOutlined sx={{ fontSize: 16, mt: '2px' }} />
              <Typography variant="caption">
                <b>{tl('How it works')}:</b> {tl('Enable / Clear section sets every screen in that section. On each screen, View is the parent: Add, Edit and Delete need View, and turning View off removes them. Screens marked Shared use the same permission, so they change together.')}
              </Typography>
            </Stack>
          </Box>

          <Box sx={{ p: { xs: 1.5, md: 2 }, maxHeight: { lg: 'calc(100vh - 300px)' }, overflow: 'auto' }}>
            {sections.map((section) => {
              const grantable = section.allPages.filter((p) => !p.platformOnly);
              const platformSection = grantable.length === 0;
              const grantedCount = stats.full ? grantable.length : grantable.filter((p) => pageHasView(draftPerms, p)).length;
              const allGranted = grantable.length > 0 && grantedCount === grantable.length;
              return (
                <Box key={section.sectionId} sx={{ mb: 2.5 }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: section.sectionAccent }} />
                    <Typography variant="subtitle2" fontWeight={800}>{tl(section.sectionLabel)}</Typography>
                    <Tooltip title={platformSection
                      ? tl('Platform-only screens — not grantable to tenant roles')
                      : `${grantedCount} of ${grantable.length} screens in this section have View`}
                    >
                      <Chip
                        size="small"
                        label={platformSection ? tl('Platform only') : `${grantedCount}/${grantable.length} ${tl('screens')}`}
                        color={!platformSection && grantedCount > 0 ? 'primary' : 'default'}
                        variant={allGranted ? 'filled' : 'outlined'}
                        sx={{ height: 20, fontSize: '0.65rem' }}
                      />
                    </Tooltip>
                    <Box flex={1} />
                    {!platformSection && (
                      <Tooltip title={stats.full ? tl('Turn off Full access to change sections') : ''}>
                        <span>
                          {grantedCount > 0 ? (
                            <Button
                              size="small"
                              color="warning"
                              disabled={stats.full}
                              onClick={() => clearSection(section.sectionLabel, section.allPages)}
                              sx={{ textTransform: 'none', fontWeight: 700 }}
                            >
                              {tl('Clear section')}
                            </Button>
                          ) : null}
                          {!allGranted && (
                            <Button
                              size="small"
                              disabled={stats.full}
                              onClick={() => enableSection(section.allPages)}
                              sx={{ textTransform: 'none', fontWeight: 700 }}
                            >
                              {tl('Enable section')}
                            </Button>
                          )}
                        </span>
                      </Tooltip>
                    )}
                  </Stack>

                  <Stack spacing={1}>
                    {section.pages.map((page) => {
                      const actions = getPageActions(page);
                      const locked = page.platformOnly || stats.full;
                      const viewed = !page.platformOnly && (stats.full || pageHasView(draftPerms, page));
                      const shared = page.platformOnly ? [] : sharedScreens(page);
                      return (
                        <Box
                          key={page.id}
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) auto' },
                            gap: 1.25,
                            alignItems: 'center',
                            p: 1.5,
                            borderRadius: 2.5,
                            border: 1,
                            borderColor: viewed ? alpha(section.sectionAccent, 0.35) : 'divider',
                            bgcolor: page.platformOnly
                              ? alpha(theme.palette.text.primary, 0.03)
                              : viewed ? alpha(section.sectionAccent, 0.05) : 'background.paper',
                            transition: 'all 0.15s ease',
                          }}
                        >
                          <Box minWidth={0}>
                            <Stack direction="row" spacing={0.75} alignItems="center">
                              <Typography variant="body2" fontWeight={700} noWrap>{tl(page.pageLabel)}</Typography>
                              {page.platformOnly && (
                                <Tooltip title={tl('Only the platform Super Admin account can open this screen')}>
                                  <Chip size="small" icon={<Lock sx={{ fontSize: '12px !important' }} />} label={tl('Platform only')} sx={{ height: 18, fontSize: '0.62rem' }} />
                                </Tooltip>
                              )}
                              {shared.length > 0 && (
                                <Tooltip title={`Uses the same permission as: ${shared.map((p) => p.pageLabel).join(', ')} — changing one changes them all`}>
                                  <Chip size="small" variant="outlined" icon={<LinkIcon sx={{ fontSize: '12px !important' }} />} label={`${tl('Shared')} · ${shared.length}`} sx={{ height: 18, fontSize: '0.62rem' }} />
                                </Tooltip>
                              )}
                            </Stack>
                            <Typography variant="caption" color="text.secondary" noWrap display="block">
                              {page.path}
                              {page.description ? ` · ${tl(page.description)}` : ''}
                            </Typography>
                          </Box>
                          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap justifyContent="flex-end">
                            <Chip
                              size="small"
                              icon={viewed ? <CheckCircle sx={{ fontSize: '14px !important' }} /> : undefined}
                              label={tl('View')}
                              onClick={locked ? undefined : () => toggleView(page, !viewed)}
                              color={viewed ? 'primary' : 'default'}
                              variant={viewed ? 'filled' : 'outlined'}
                              disabled={page.platformOnly}
                              sx={{ fontWeight: viewed ? 800 : 500, height: 28, cursor: locked ? 'default' : 'pointer' }}
                            />
                            {actions.map((action) => {
                              const on = viewed && (stats.full || pageHasAction(draftPerms, action));
                              const meta = privilegeActionMeta(action.kind);
                              const isDelete = action.kind === 'delete';
                              const needsView = !viewed && !page.platformOnly;
                              const tip = page.platformOnly
                                ? ''
                                : stats.full ? tl('Granted by Full access')
                                  : needsView ? tl('Turn on View first — write actions need View')
                                    : on ? `${tl('Click to remove')} ${action.label}`
                                      : isDelete ? tl('Delete permanently removes records — you will be asked to confirm') : `${tl('Click to grant')} ${action.label}`;
                              return (
                                <Tooltip key={action.kind} title={tip}>
                                  <span>
                                    <Chip
                                      size="small"
                                      icon={isDelete ? <DeleteOutline sx={{ fontSize: '14px !important' }} /> : on ? <CheckCircle sx={{ fontSize: '14px !important' }} /> : undefined}
                                      label={tl(action.label)}
                                      onClick={locked || needsView ? undefined : () => toggleAction(page, action, !on)}
                                      color={on ? meta.color : isDelete ? 'error' : 'default'}
                                      variant={on ? 'filled' : 'outlined'}
                                      disabled={page.platformOnly || needsView}
                                      sx={{
                                        fontWeight: on ? 800 : 500,
                                        height: 28,
                                        cursor: locked || needsView ? 'default' : 'pointer',
                                        ...(isDelete && !on && { borderStyle: 'dashed' }),
                                      }}
                                    />
                                  </span>
                                </Tooltip>
                              );
                            })}
                          </Stack>
                        </Box>
                      );
                    })}
                  </Stack>
                </Box>
              );
            })}
            {sections.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                {tl('No screens match this search or filter')}
              </Typography>
            )}
          </Box>
        </Paper>

        {/* ── Summary ── */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: 1,
            borderColor: 'divider',
            overflow: 'hidden',
            position: { lg: 'sticky' },
            top: { lg: 16 },
            display: { xs: 'none', lg: 'block' },
          }}
        >
          <Box sx={{ px: 2, py: 1.75, bgcolor: alpha('#0f766e', 0.06), borderBottom: 1, borderColor: 'divider' }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <CheckCircle sx={{ fontSize: 18, color: '#0f766e' }} />
              <Typography variant="subtitle2" fontWeight={800}>{tl('Unlocks')}</Typography>
            </Stack>
          </Box>
          <Box sx={{ p: 2 }}>
            <Stack spacing={1.25} sx={{ mb: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">{tl('Screens with View')}</Typography>
                <Typography variant="h5" fontWeight={800}>{stats.viewScreens}<Typography component="span" color="text.secondary"> / {stats.totalScreens}</Typography></Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">{tl('Screens with write actions')}</Typography>
                <Typography variant="h5" fontWeight={800}>{stats.writeScreens}<Typography component="span" color="text.secondary"> / {stats.totalScreens}</Typography></Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">{tl('Permissions granted')}</Typography>
                <Typography variant="h5" fontWeight={800}>{stats.grantedActions}<Typography component="span" color="text.secondary"> / {stats.totalActions}</Typography></Typography>
                <Typography variant="caption" color="text.disabled">{tl('View + each action, counted per screen')}</Typography>
              </Box>
            </Stack>
            <Divider sx={{ mb: 1.5 }} />
            <Typography variant="caption" color="text.secondary" display="block" mb={1}>
              {tl('Screens this role can open')} ({unlockPages.length})
            </Typography>
            <Stack spacing={0.75} sx={{ maxHeight: 320, overflow: 'auto' }}>
              {unlockPages.map((p) => (
                <Stack key={p.id} direction="row" spacing={1} alignItems="center">
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: p.sectionAccent || 'primary.main' }} />
                  <Typography variant="caption" fontWeight={600}>{tl(p.pageLabel)}</Typography>
                </Stack>
              ))}
              {!unlockPages.length && (
                <Typography variant="caption" color="text.disabled">{tl('No screens unlocked')}</Typography>
              )}
            </Stack>
            {selectedRole && (
              <Button fullWidth size="small" sx={{ mt: 2 }} startIcon={<ContentCopy />} onClick={() => openRoleDialog(null, selectedRole)}>
                {tl('Clone role')}
              </Button>
            )}
          </Box>
        </Paper>
      </Box>

      {/* ── Role details (create / edit / clone) ── */}
      <Dialog open={roleDialog} onClose={() => setRoleDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingRole ? `${tl('Edit Role')} — ${editingRole.name}` : tl('New Role')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {editingRole?.isSystem && (
              <Alert severity="info" icon={<Lock fontSize="small" />}>
                {tl('System role: name, slug and status are fixed and it cannot be deleted. You can change its description here and its screen access in the matrix.')}
                {editingRole.fullAccessLocked ? ` ${tl('As the tenant owner role it always keeps Full access.')}` : ''}
              </Alert>
            )}
            {roleError && <Alert severity="error">{roleError}</Alert>}
            <TextField
              label={tl('Name')}
              required
              value={roleForm.name}
              disabled={!!editingRole?.isSystem}
              onChange={(e) => {
                const name = sanitizeRoleName(e.target.value);
                setRoleForm((f) => ({ ...f, name, slug: editingRole ? f.slug : slugFromRoleName(name) }));
                setRoleError('');
              }}
              onBlur={() => setRoleTouched((s) => ({ ...s, name: true }))}
              error={!!nameMsg}
              helperText={nameMsg || tl('Letters and spaces only, 3–50 characters')}
              fullWidth
            />
            <TextField
              label={tl('Slug')}
              required
              value={roleForm.slug}
              disabled={!!editingRole}
              onChange={(e) => { setRoleForm((f) => ({ ...f, slug: sanitizeRoleSlug(e.target.value) })); setRoleError(''); }}
              onBlur={() => setRoleTouched((s) => ({ ...s, slug: true }))}
              error={!!slugMsg}
              helperText={slugMsg || (editingRole
                ? tl('Unique code — cannot be changed after the role is created')
                : tl('Generated from the name. Lowercase letters with underscores between words; cannot be changed later'))}
              InputProps={editingRole ? { endAdornment: <InputAdornment position="end"><Lock fontSize="small" /></InputAdornment> } : undefined}
              fullWidth
            />
            <TextField
              label={tl('Description')}
              value={roleForm.description}
              onChange={(e) => setRoleForm((f) => ({ ...f, description: e.target.value.slice(0, ROLE_DESCRIPTION_MAX) }))}
              helperText={`${roleForm.description.length}/${ROLE_DESCRIPTION_MAX}`}
              fullWidth
              multiline
              rows={2}
            />
            {!editingRole && (
              <Typography variant="caption" color="text.secondary">
                {roleForm.permissions.length
                  ? `${tl('Starts with the copied privileges')}: ${roleCoverage(roleForm.permissions).viewScreens} ${tl('screens')}. ${tl('Adjust them in the matrix after saving.')}`
                  : tl('Starts with no access. Grant screens in the matrix after saving.')}
              </Typography>
            )}
            {editingRole && (
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Chip size="small" label={`${editingRole.userCount || 0} ${tl('users assigned')}`} />
                <Chip size="small" label={`${roleCoverage(normalizeRolePermissions(editingRole.permissions)).viewScreens}/${GRANTABLE_PAGES.length} ${tl('screens')}`} />
                {editingRole.hasDefaults && !editingRole.fullAccessLocked && (
                  <Button
                    size="small"
                    color="warning"
                    startIcon={<RestartAlt />}
                    onClick={() => {
                      if (blockDemoWrite()) return;
                      setRoleDialog(false);
                      setConfirm({
                        title: `Reset ${editingRole.name} to defaults?`,
                        body: <Typography variant="body2">{tl('Replaces its current privileges with the standard set for this system role. Unsaved changes in the matrix are discarded.')}</Typography>,
                        confirmLabel: 'Reset to defaults',
                        color: 'warning',
                        onConfirm: () => resetRoleMutation.mutate(editingRole._id),
                      });
                    }}
                  >
                    {tl('Reset privileges to defaults')}
                  </Button>
                )}
              </Stack>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleDialog(false)}>{tl('Cancel')}</Button>
          <Button
            variant="contained"
            disabled={roleFormBlocked}
            onClick={() => {
              setRoleTouched({ name: true, slug: true });
              if (blockDemoWrite()) return;
              saveRoleMutation.mutate();
            }}
          >
            {editingRole ? tl('Save details') : tl('Create role')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirm} onClose={() => setConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{confirm?.title}</DialogTitle>
        <DialogContent>{confirm?.body}</DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)}>{tl('Cancel')}</Button>
          <Button
            variant="contained"
            color={confirm?.color || 'primary'}
            onClick={() => { confirm?.onConfirm(); setConfirm(null); }}
          >
            {confirm ? tl(confirm.confirmLabel) : ''}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? <Alert severity={toast.severity} onClose={() => setToast(null)} variant="filled">{toast.message}</Alert> : undefined}
      </Snackbar>

      <Dialog open={demoBlockedOpen} onClose={() => setDemoBlockedOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{tl('Save not allowed')}</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mt: 0.5 }}>
            {DEMO_WRITE_BLOCKED_MESSAGE}
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            {tl('Demo tenants cannot change roles or privileges. Only real tenants and Super Admin can save this screen.')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setDemoBlockedOpen(false)}>{tl('OK')}</Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
}
